/**
 * usePitchBookUpload — upload state machine for PitchBook CSV files.
 *
 * States: idle → uploading → finalizing → polling → ready | error
 *   - uploading  : XHR PUT to presigned S3 URL (progress events)
 *   - finalizing : POST /complete (enqueue parse job)
 *   - polling    : GET /status every 2 s until "ready" or "failed"
 *   - ready      : parse complete; invalidates the company list query
 *   - error      : captures message; call reset() to retry
 *
 * Deliberately no column-mapping step — PitchBook CSV headers are fixed
 * and handled server-side by domain.py.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createPitchBookClient } from "../../clients/pitchbook.client";
import { xhrPutToS3 } from "../../routes/research/new-import/useNewImport";

export type UploadState = "idle" | "uploading" | "finalizing" | "polling" | "ready" | "error";

export interface UsePitchBookUploadResult {
  state: UploadState;
  progress: number;
  error: string | null;
  /** The company list the upload created, once `createUpload` answered. */
  listId: string | null;
  startUpload: (file: File, searchId: string, opts?: UploadOptions) => Promise<void>;
  reset: () => void;
}

export interface UploadOptions {
  /** Name for the company list the upload creates. Omitted = the filename. */
  name?: string;
  /** `csv_upload` from the web app's import, `chopbook` (default) otherwise. */
  source?: "chopbook" | "csv_upload";
}

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_TICKS = 300; // 10 minutes max

export function usePitchBookUpload(): UsePitchBookUploadResult {
  const [state, setState] = useState<UploadState>("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [listId, setListId] = useState<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTicksRef = useRef(0);
  const queryClient = useQueryClient();

  const clearPoll = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollTicksRef.current = 0;
  }, []);

  // Clean up on unmount
  useEffect(() => () => clearPoll(), [clearPoll]);

  const reset = useCallback(() => {
    clearPoll();
    setState("idle");
    setProgress(0);
    setError(null);
    setListId(null);
  }, [clearPoll]);

  const startUpload = useCallback(
    async (file: File, searchId: string, opts: UploadOptions = {}) => {
      clearPoll();
      setListId(null);
      setState("uploading");
      setProgress(0);
      setError(null);

      const client = createPitchBookClient({ apiBase: API_BASE });

      try {
        // Step 1: create import row + presigned PUT URL
        const created = await client.createUpload(searchId, file.name, opts);
        setListId(created.import_id);

        // Step 2: PUT file to S3 with progress tracking
        const putResult = await xhrPutToS3({
          url: created.upload_url,
          file,
          contentType: "text/csv",
          onProgress: setProgress,
        });

        if (!putResult.ok) {
          throw new Error(`S3 upload failed (HTTP ${putResult.status ?? "unknown"})`);
        }

        // Step 3: notify API to enqueue parse
        setState("finalizing");
        await client.completeUpload(searchId, created.import_id);

        // Step 4: poll status until ready or failed
        setState("polling");
        pollTicksRef.current = 0;

        pollIntervalRef.current = setInterval(() => {
          pollTicksRef.current += 1;
          if (pollTicksRef.current > MAX_POLL_TICKS) {
            clearPoll();
            setState("error");
            setError("Upload timed out. Please try again.");
            return;
          }

          client.getUploadStatus(searchId, created.import_id).then((status) => {
            if (status.status === "ready") {
              clearPoll();
              setState("ready");
              void queryClient.invalidateQueries({
                queryKey: ["pitchbook", "companies", searchId],
              });
              // The upload IS a company list: refresh every list view.
              void queryClient.invalidateQueries({ queryKey: ["company-lists"] });
            } else if (status.status === "failed") {
              clearPoll();
              setState("error");
              setError(status.error_message ?? "CSV parsing failed. Please try again.");
            }
          }).catch((err: unknown) => {
            const msg = err instanceof Error ? err.message : "Polling error";
            clearPoll();
            setState("error");
            setError(msg);
          });
        }, POLL_INTERVAL_MS);
      } catch (err: unknown) {
        clearPoll();
        setState("error");
        setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
      }
    },
    [clearPoll, queryClient],
  );

  return { state, progress, error, listId, startUpload, reset };
}
